import '@common/modules/workspaces/host'

import { WorkspaceService } from '@graft/nuget-gc.webportals.workspaces'
import type { StateCreator } from 'zustand'

import { getSessionToken } from '@common/core/helpers'
import { detectUserRegion } from '@common/core/utils/detectUserRegion'
import {
  deleteWorkspaceMembership,
  inviteProjectMember as inviteProjectMemberApi,
  inviteWorkspaceMember as inviteWorkspaceMemberApi,
} from '@common/modules/memberships/memberships'
import { getProjectMembers } from '@common/modules/projects/projects'
import { useProjectsStore } from '@common/modules/projects/stores'
import { assertNoInviteEmailConflict } from '@common/modules/settings/utils/assertNoInviteEmailConflict'
import { InviteEmailConflictError } from '@common/modules/settings/utils/InviteEmailConflictError'
import { normalizeInviteEmail } from '@common/modules/settings/utils/normalizeInviteEmail'
import { useWorkspacesStore } from '@common/modules/workspaces/stores/workspaces.store'

import { workspaceMemberToObject } from '../../objects'
import type { WorkspaceStore } from '../types'
import { buildPendingWorkspaceMember } from '../utils/buildPendingWorkspaceMember'

import type { WorkspaceMembersActionsSlice } from './workspace-members-actions.slice.type'

const ensureWorkspaceMembersLoaded = async (get: () => WorkspaceStore): Promise<void> => {
  if (get().members.length > 0) {
    return
  }

  await get().getWorkspaceMembers()
}

export const createWorkspaceMembersActionsSlice: StateCreator<
  WorkspaceStore,
  [['zustand/immer', never]],
  [],
  WorkspaceMembersActionsSlice
> = (set, get) => ({
  getWorkspaceMembers: async () => {
    const workspace = get().workspace
    if (!workspace) return

    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
      state.members = []
    })

    try {
      const token = await getSessionToken()
      const members = await WorkspaceService.getWorkspaceMembers(token, workspace.id)
      const result = await Promise.all((members ?? []).map(workspaceMemberToObject))

      set((state: WorkspaceStore) => {
        state.members = result
        if (state.workspace) {
          state.workspace.memberCount = result.length
        }
      })
    } catch (err) {
      set((state: WorkspaceStore) => {
        state.members = []
        state.error = err instanceof Error ? err.message : 'Failed to load workspace members'
        if (state.workspace) {
          state.workspace.memberCount = 0
        }
      })
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },

  inviteWorkspaceMember: async (email: string, role, callbacks) => {
    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const workspace = get().workspace ?? useWorkspacesStore.getState().currentWorkspace
      if (!workspace) {
        throw new Error('No current workspace')
      }

      await ensureWorkspaceMembersLoaded(get)
      assertNoInviteEmailConflict(get().members, email, 'workspace')

      await inviteWorkspaceMemberApi(workspace.id, role, email, detectUserRegion())
      callbacks?.onSuccess?.()
    } catch (err) {
      if (err instanceof InviteEmailConflictError) {
        callbacks?.onError?.(err)
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to invite member'
      set((state: WorkspaceStore) => {
        state.error = errorMessage
      })
      callbacks?.onError?.(errorMessage)
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },

  inviteMember: async (email: string, role, callbacks) => {
    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const workspace = get().workspace
      if (!workspace) {
        throw new Error('No current workspace')
      }

      const currentProject = useProjectsStore.getState().currentProject
      if (!currentProject) {
        throw new Error('No current project selected')
      }

      const projectMembers = await getProjectMembers(currentProject.id)
      assertNoInviteEmailConflict(projectMembers, email, 'project')

      await inviteProjectMemberApi(currentProject.id, role, email, detectUserRegion())

      const emailLower = normalizeInviteEmail(email)

      set((state: WorkspaceStore) => {
        if (!state.members.some((member) => normalizeInviteEmail(member.email) === emailLower)) {
          state.members.unshift(buildPendingWorkspaceMember(email, role, currentProject.id))
        }
        if (state.workspace) {
          state.workspace.memberCount = state.members.length
        }
      })
      callbacks?.onSuccess?.()
    } catch (err) {
      if (err instanceof InviteEmailConflictError) {
        callbacks?.onError?.(err)
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to invite member'
      set((state: WorkspaceStore) => {
        state.error = errorMessage
      })
      callbacks?.onError?.(errorMessage)
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },

  removeMember: async (memberId: string) => {
    const workspace = get().workspace
    if (!workspace) return

    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
    })

    const previousMembers = get().members

    try {
      await deleteWorkspaceMembership(workspace.id, memberId)

      set((state: WorkspaceStore) => {
        state.members = state.members.filter((m) => m.id !== memberId)
        if (state.workspace) {
          state.workspace.memberCount = state.members.length
        }
      })
    } catch (err) {
      set((state: WorkspaceStore) => {
        state.members = previousMembers
        state.error = err instanceof Error ? err.message : 'Failed to remove member'
        if (state.workspace) {
          state.workspace.memberCount = previousMembers.length
        }
      })
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },

  updateMemberRole: async (memberId: string, role) => {
    const workspace = get().workspace
    if (!workspace) return

    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const token = await getSessionToken()
      await WorkspaceService.updateWorkspaceMemberLevel(token, workspace.id, memberId, role)

      set((state: WorkspaceStore) => {
        const member = state.members.find((m) => m.id === memberId)
        if (member) {
          member.role = role
        }
      })
    } catch (err) {
      set((state: WorkspaceStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update member role'
      })
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },
})
